import { Typography, Form, Button, message, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import VerificationInput from "react-verification-input";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getLoggedUserInfo, type UserState } from "redux-context/user";
import { postSignupCode, sendVerificationCode } from "services/SauceService";
import { ReactComponent as Logo } from "assets/images/logo.svg";
import "./Verification.scss";
import {
  BusinessState,
  getBusinessess,
  getBusinessInfo,
} from "redux-context/business";
import { getDomainMetadata } from "redux-context/domain";
import {
  trackVerificationConfirmClicked,
  trackVerificationResendClicked,
  trackVerificationSuccess,
  trackVerifyEmailPageViewed,
} from "analytics";
import ReCAPTCHA from "react-google-recaptcha";

enum VerificationType {
  EMAIL = "email",
}

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const SENT_EMAIL_KEY = "verification_sent_email";

function markEmailAsSent(email?: string) {
  if (typeof email === "string") {
    try {
      localStorage.setItem(SENT_EMAIL_KEY, email);
    } catch (e) {
      // ignore localStorage failures
    }
  }
  return localStorage.getItem(SENT_EMAIL_KEY);
}

const VerificationCode: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const dispatch = useDispatch<any>();

  const [sending, setSending] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [hasSentCode, setHasSentCode] = useState<boolean>(false);
  const captchaRef = useRef<any>(null);

  const [resendCooldown, setResendCooldown] = useState<number>(0);

  const [searchParams] = useSearchParams();
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const hasTrackedVerifyPageViewedRef = useRef(false);
  const businessId = (business as any)?._id ?? null;
  const planType = (subscription as any)?.planName ?? null;
  const currency =
    (subscription as any)?.stripeDetails?.nextPaymentDetails?.currency ?? null;

  // URLSearchParams may decode '+' into a space (application/x-www-form-urlencoded behavior).
  // Restore '+' characters in the extracted email so addresses like 'test+something@...' keep the plus.
  const rawEmail = searchParams.get("email") || undefined;
  const emailFromQuery = rawEmail ? rawEmail.replace(/ /g, "+") : undefined;

  const businessName = searchParams.get("businessName") || undefined;
  const partner = searchParams.get("campaign") || undefined;

  const isCodeComplete = useMemo(
    () => (code || "").replace(/\D/g, "").length === CODE_LENGTH,
    [code],
  );

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType,
      currency,
    }),
    [
      businessId,
      currency,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      planType,
    ],
  );

  useEffect(() => {
    form.resetFields();
  }, [form, loggedUser]);

  useEffect(() => {
    if (hasTrackedVerifyPageViewedRef.current) {
      return;
    }

    hasTrackedVerifyPageViewedRef.current = true;
    trackVerifyEmailPageViewed(analyticsContext);
  }, [analyticsContext]);

  // Cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const id = window.setInterval(() => {
      setResendCooldown((v) => Math.max(0, v - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const sendCode = async (isResend = false) => {
    if (!emailFromQuery) {
      message.error("No email to send to.");
      return;
    }

    if (!captchaToken) {
      message.error("Please complete CAPTCHA.");
      return;
    }

    if (isResend && resendCooldown > 0) {
      return;
    }

    if (isResend) {
      trackVerificationResendClicked(analyticsContext);
    }

    setSending(true);
    try {
      await sendVerificationCode({
        type: VerificationType.EMAIL,
        contact: emailFromQuery,
        captchaToken,
      });

      markEmailAsSent(emailFromQuery);
      setHasSentCode(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      message.success(isResend ? "Code resent." : "Verification code sent.");
      captchaRef.current?.reset();
      setCaptchaToken("");
    } catch (err: any) {
      captchaRef.current?.reset();
      setCaptchaToken("");
      if (err?.response?.status === 403) {
        message.error("Verification failed. Please complete CAPTCHA again.");
      } else {
        message.error(
          isResend
            ? "Error resending verification code."
            : "Error sending verification code."
        );
      }
    } finally {
      setSending(false);
    }
  };

  const onClickVerify = async (value?: string) => {
    if (!hasSentCode) {
      message.error("Send a verification code first.");
      return;
    }

    const verificationCode = (
      code ||
      form.getFieldValue("verificationCode") ||
      "" ||
      value
    )
      .toString()
      .replace(/\D/g, "");

    if (verificationCode.length !== CODE_LENGTH) {
      message.error(`Please enter the ${CODE_LENGTH}-digit code.`);
      return;
    }

    if (!emailFromQuery) {
      message.error("No email provided.");
      return;
    }

    trackVerificationConfirmClicked(analyticsContext);

    const params = {
      email: emailFromQuery,
      code: verificationCode,
      businessName: businessName,
      partner: partner,
    };

    setVerifying(true);
    try {
      const response: any = await postSignupCode(params);

      if (response && response.accessToken) {
        localStorage.setItem("token", response.accessToken);
        if (response.businessId)
          localStorage.setItem("businessId", response.businessId);

        dispatch(getBusinessess());
        dispatch(getBusinessInfo());
        dispatch(getDomainMetadata());
        dispatch(getLoggedUserInfo());
        trackVerificationSuccess(analyticsContext);
        navigate("/");
        return;
      }

      message.error("Invalid code. Please try again.");
    } catch (err) {
      message.error("Error verifying the code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const resendCode = () => {
    setCode("");
    form.setFieldsValue({ verificationCode: "" });
    captchaRef.current?.reset();
    setCaptchaToken("");
    setHasSentCode(false);
  };

  const onChangeEmail = () => {
    // Safer default: go back to the previous step (landing/signup form).
    navigate(-1);
  };

  return (
    <div className="verification-page">
      <Spin spinning={sending}>
        <div className="verification-card">
          <Logo className="verification-logo" />

          <div className="verification-header">
            <Typography.Title level={3} className="verification-title">
              Verify your email
            </Typography.Title>

            <Typography.Text className="verification-subtitle">
              {hasSentCode ? "We sent a" : "Send a"} {CODE_LENGTH}-digit code to{" "}
              <span className="verification-email">
                {emailFromQuery || "your email"}
              </span>
              .
            </Typography.Text>

            <Typography.Text className="verification-helper">
              {hasSentCode
                ? "Enter the code below to confirm your email address."
                : "Complete CAPTCHA and send a verification code first."}
            </Typography.Text>
          </div>

          {!hasSentCode && (
            <>
              <div style={{ marginBottom: 16 }}>
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY || ""}
                  onChange={(token: string | null) => setCaptchaToken(token || "")}
                  onExpired={() => {
                    setCaptchaToken("");
                    message.error("CAPTCHA expired. Please verify again.");
                  }}
                  onErrored={() => {
                    setCaptchaToken("");
                    message.error("Unable to load CAPTCHA. Please try again.");
                  }}
                />
              </div>

              <Button
                type="primary"
                className="verification-button"
                loading={sending}
                disabled={!captchaToken || sending}
                style={{ width: "100%", marginBottom: 16 }}
                onClick={() => sendCode(false)}
              >
                Send code
              </Button>
            </>
          )}

          {hasSentCode && (
            <Form
              form={form}
              layout="vertical"
              className="verification-form"
              onFinish={onClickVerify}
            >
              <div className="verification-controls">
                <div className="code-box">
                  <Form.Item
                    label="Verification code"
                    name="verificationCode"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the code!",
                      },
                    ]}
                  >
                    <div
                      className="verification-code-inputs"
                      role="group"
                      aria-label="verification code"
                    >
                      <VerificationInput
                        length={CODE_LENGTH}
                        validChars="0-9"
                        inputProps={{
                          inputMode: "numeric",
                          autoComplete: "one-time-code",
                          "aria-label": "verification digit",
                          style: {
                            fontSize: "16px",
                          },
                        }}
                        value={code}
                        onChange={(val: string) => setCode(val)}
                        onComplete={(value) => {
                          onClickVerify(value);
                        }}
                      />
                    </div>
                  </Form.Item>
                </div>

                <Button
                  type="primary"
                  className="verification-button"
                  htmlType="submit"
                  loading={verifying}
                  disabled={!isCodeComplete || verifying || !hasSentCode}
                  style={{ width: "100%" }}
                >
                  Confirm email
                </Button>

                <div className="verification-secondary">
                  <button
                    type="button"
                    className="resend-link"
                    onClick={resendCode}
                    disabled={sending || verifying}
                  >
                    Send new code
                  </button>

                  <span
                    className="secondary-divider"
                    aria-hidden="true"
                    style={{
                      display: "none",
                    }}
                  >
                    •
                  </span>

                  <button
                    type="button"
                    className="change-email-link"
                    onClick={onChangeEmail}
                    disabled={sending || verifying}
                    style={{
                      display: "none",
                    }}
                  >
                    Change email
                  </button>
                </div>

                <Typography.Text className="verification-tip">
                  Tip: check your spam/junk folder if you don’t see the email.
                </Typography.Text>
              </div>
            </Form>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default VerificationCode;
